import React, { Component } from "react";
import * as XLSX from "xlsx";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";

// Local OMR Excel files — drop .xlsx/.xls files in the ./data folder.
// Each file becomes an "exam" in the dropdown (named after the file).
// Excel writes a temporary owner-lock file (e.g. "~$Name.xlsx") while a workbook
// is open. Skip those — they aren't real spreadsheets and break the dropdown/match.
const isTempExcel = (k) => /(^|\/)~\$/.test(k);

let LOCAL_FILES = [];
try {
  const ctx = require.context("./data", false, /\.(xlsx|xls)$/);
  LOCAL_FILES = ctx.keys().filter((k) => !isTempExcel(k)).map((k) => ({
    name: k.replace(/^\.\//, "").replace(/\.(xlsx|xls)$/i, ""),
    url: ctx(k),
  }));
} catch (e) { /* no ./data folder or no excel files */ }

// Subject-wise files — ./data/subject_wise (matched to the exam by file name).
let SUBJECT_FILES = [];
try {
  const sctx = require.context("./data/subject_wise", false, /\.(xlsx|xls)$/);
  SUBJECT_FILES = sctx.keys().filter((k) => !isTempExcel(k)).map((k) => ({
    name: k.replace(/^\.\//, "").replace(/\.(xlsx|xls)$/i, ""),
    url: sctx(k),
  }));
} catch (e) { /* no subject_wise folder */ }

// Same source as the Management Report cascading dropdowns.
const GET_SCOPE = gql`
  query($institution_id: Int!) {
    getBranchesList(institution_id: $institution_id) { id }
    getInstituteBranchClasses(institute_id: $institution_id) { class_id class branch_id }
    getSectionsByBranchClasses(institute_id: $institution_id) { id section_name branch_id class_id }
  }
`;

// Roll Number -> student name (settings/non-academic-students service).
const GET_STUDENTS_BY_ROLL = gql`
  query($institution_id: Int!, $branch_id: String) {
    getCombinedAcademicAndNonAcademicStudents(institution_id: $institution_id, branch_id: $branch_id) {
      roll_no
      student_name
      class_name
      section_name
      contact_no
    }
  }
`;

/*
  OMR Results — export in the OMR scanner format:
  File, Roll Number, Total Questions, Answered, Correct, Wrong, Blank,
  Flagged, +Marks, -Marks, Total Marks, Marks Obtained, Confidence, Q1..Q75.
  Sample rows below; wire `rows` to a backend query later.
*/

const isQCol = (k) => /^Q\d+$/i.test(k);

class OmrResultsSection extends Component {
  state = { activeTab: "results", showQuestions: false, filters: { exam: "", class: "", section: [] }, scope: { classes: [], sections: [] }, uploadedRows: null, uploadName: "", rollMap: {}, sectionOpen: false, subjectRows: null, sortKey: "", sortDir: "asc", page: 1, pageSize: 25 };

  // Sort a row set by the active column (numeric-aware), respecting direction.
  sortRows = (rows) => {
    const { sortKey, sortDir } = this.state;
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const num = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = parseFloat(String(v).replace(/,/g, ""));
      return Number.isNaN(n) ? null : n;
    };
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = num(av);
      const bn = num(bv);
      if (an !== null && bn !== null) return (an - bn) * dir;
      if (an !== null) return -1 * dir;
      if (bn !== null) return 1 * dir;
      return String(av == null ? "" : av).localeCompare(String(bv == null ? "" : bv), undefined, { numeric: true }) * dir;
    });
  };

  // Toggle sort on a column: same column flips direction, new column starts asc.
  onSort = (col) => {
    this.setState((s) =>
      s.sortKey === col
        ? { sortDir: s.sortDir === "asc" ? "desc" : "asc" }
        : { sortKey: col, sortDir: "asc" }
    );
  };

  setPage = (page) => this.setState({ page });
  setPageSize = (pageSize) => this.setState({ pageSize, page: 1 });

  // Subject-wise rows enriched + filtered, with derived columns (from data/subject_wise file).
  renderSubjectReport = (S) => {
    if (!this.state.subjectRows) {
      return <div style={S.card}><div style={{ padding: "28px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No subject-wise file for this exam (data/subject_wise/).</div></div>;
    }
    // Enrich (Name/Class/Section by CANDIDATE ID) + filter by Class/Section.
    const enriched = this.enrich(this.state.subjectRows);
    const rows = this.filterByScope(enriched);
    const { base, q } = this.deriveColumns(enriched);
    const cols = [...base, ...q];
    return (
      <div style={S.card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{cols.map((c) => <th key={c} style={S.th}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={cols.length || 1} style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: "28px 0" }}>No results.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 ? "#faf5ff" : "#fff" }}>
                  {cols.map((c) => {
                    const isQ = isQCol(c);
                    const hi = c === "Roll Number" || c === "Student Name";
                    return <td key={c} style={{ ...S.td, textAlign: isQ ? "center" : "left", fontWeight: hi ? 600 : (isQ ? 700 : 400), color: isQ ? "#7c3aed" : (hi ? "#0f172a" : "#334155") }}>{r[c] !== undefined && r[c] !== null && r[c] !== "" ? r[c] : (isQ ? "" : "-")}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  sectionRef = React.createRef();

  handleOutside = (e) => {
    if (this.sectionRef.current && !this.sectionRef.current.contains(e.target)) this.setState({ sectionOpen: false });
  };

  // Changing class re-selects all of that class's sections (default = all).
  onClassChange = (value) => {
    const sc = this.state.scope || {};
    const classByName = {}; (sc.classes || []).forEach((c) => { classByName[c.name] = c.id; });
    const cid = value ? classByName[value] : null;
    const secs = [...new Set((sc.sections || [])
      .filter((s) => !cid || String(s.class_id) === String(cid))
      .map((s) => s.name).filter(Boolean))];
    this.setState({ filters: { ...this.state.filters, class: value, section: secs }, page: 1 });
  };

  // Switch tab and reset the filters/view (class cleared, all sections, questions hidden).
  setTab = (tab) => {
    const allSecs = [...new Set((this.state.scope.sections || []).map((s) => s.name).filter(Boolean))];
    this.setState({ activeTab: tab, showQuestions: false, sectionOpen: false, filters: { ...this.state.filters, class: "", section: allSecs }, page: 1 });
  };

  // Raw data source: uploaded/local Excel > prop rows > empty.
  allRows = () => this.state.uploadedRows || this.props.rows || [];

  // Add Student Name / Class / Section (looked up by Roll Number) to any row set.
  enrich = (rows) => {
    const map = this.state.rollMap || {};
    return (rows || []).map((r) => {
      const rawRoll = r["Roll Number"] != null && r["Roll Number"] !== "" ? r["Roll Number"] : r["CANDIDATE ID"];
      const roll = rawRoll != null ? String(rawRoll).trim() : "";
      const hit = map[roll] || {};
      return {
        ...r,
        "Student Name": hit.name || r["Student Name"] || "",
        Class: r.Class || hit.className || "",
        Section: r.Section || hit.sectionName || "",
      };
    });
  };
  enrichedRows = () => this.enrich(this.allRows());

  // Filter any row set by the Class / Section selections.
  filterByScope = (rows) => {
    const f = this.state.filters;
    const secs = f.section || [];
    return (rows || []).filter((r) =>
      (!f.class || String(r.Class) === f.class) &&
      (!r.Section || secs.includes(String(r.Section))));
  };

  // Derive base (non-Q, non-hidden) + Q columns from a row set.
  deriveColumns = (rows) => {
    const keys = [];
    (rows || []).forEach((r) => Object.keys(r).forEach((k) => { if (keys.indexOf(k) === -1) keys.push(k); }));
    const q = keys.filter(isQCol).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
    const HIDDEN = ["exam", "blank", "flagged", "confidence", "candidate name", "father", "group", "other", "test no", "test"];
    const base = keys.filter((k) => !isQCol(k) && !HIDDEN.includes(String(k).toLowerCase().trim()));
    const move = (col, afterCol) => {
      const i = base.indexOf(col);
      if (i === -1) return;
      base.splice(i, 1);
      const a = base.indexOf(afterCol);
      if (a > -1) base.splice(a + 1, 0, col); else base.push(col);
    };
    const idCol = base.indexOf("Roll Number") > -1 ? "Roll Number" : "CANDIDATE ID";
    move("Student Name", idCol);
    move("Class", "Student Name");
    move("Section", "Class");
    return { base, q };
  };

  // Parse an uploaded OMR Excel; stamp rows with the selected exam.
  handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const exam = this.state.filters.exam;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        let rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (exam) rows = rows.map((r) => ({ Exam: exam, ...r }));
        this.setState({ uploadedRows: rows, uploadName: file.name });
      } catch (err) { console.error("excel parse", err); alert("Could not read the Excel file."); }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  clearUpload = () => this.setState({ uploadedRows: null, uploadName: "" });

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutside);
  }

  async componentDidMount() {
    document.addEventListener("mousedown", this.handleOutside);
    try {
      const instId = parseInt(Cookies.get("institutionid")) || 118;
      const res = await this.props.client.query({ query: GET_SCOPE, variables: { institution_id: instId }, fetchPolicy: "network-only" });
      const d = res.data || {};
      const seen = new Set();
      const classes = (d.getInstituteBranchClasses || [])
        .filter((c) => c.class_id)
        .filter((c) => { const k = String(c.class_id); if (seen.has(k)) return false; seen.add(k); return true; })
        .map((c) => ({ id: c.class_id, name: c.class }));
      const sections = (d.getSectionsByBranchClasses || []).map((s) => ({ id: s.id, name: s.section_name, class_id: s.class_id }));
      const allSecs = [...new Set(sections.map((s) => s.name).filter(Boolean))];
      this.setState({ scope: { classes, sections }, filters: { ...this.state.filters, section: allSecs } });

      // Roll Number -> name map from the non-academic-students service.
      const branchIds = (d.getBranchesList || []).map((b) => b.id).join(",");
      const sres = await this.props.client.query({
        query: GET_STUDENTS_BY_ROLL,
        variables: { institution_id: instId, branch_id: branchIds || null },
        fetchPolicy: "network-only",
      });
      const list = (sres.data && sres.data.getCombinedAcademicAndNonAcademicStudents) || [];
      const rollMap = {};
      list.forEach((s) => {
        const roll = s.roll_no != null ? String(s.roll_no).trim() : "";
        if (roll) rollMap[roll] = { name: s.student_name || "", className: s.class_name || "", sectionName: s.section_name || "", phone: s.contact_no || "" };
      });
      this.setState({ rollMap });
    } catch (e) { console.error("omr scope load", e); }

    // Load the first local exam file by default.
    if (LOCAL_FILES.length) {
      this.setState({ filters: { ...this.state.filters, exam: LOCAL_FILES[0].name } });
      this.loadExamFile(LOCAL_FILES[0].name);
    }
  }

  // Load a local exam Excel by name (from the omrResults folder).
  loadExamFile = async (name) => {
    const file = LOCAL_FILES.find((f) => f.name === name);
    if (!file) { this.setState({ uploadedRows: null, uploadName: "" }); return; }
    try {
      const resp = await fetch(file.url);
      const buf = await resp.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }).map((r) => ({ Exam: name, ...r }));
      this.setState({ uploadedRows: rows, uploadName: name });
    } catch (e) { console.error("load exam file", e); }
    this.loadSubjectFile(name);
  }

  // Load the matching subject-wise file (data/subject_wise/<exam>.xlsx) for the tab.
  // Match by normalized name (ignore case/spaces); fall back to the only file if just one.
  loadSubjectFile = async (name) => {
    const norm = (s) => String(s).toLowerCase().replace(/\s+/g, "");
    const target = norm(name);
    const file = SUBJECT_FILES.find((f) => norm(f.name) === target)
      || (SUBJECT_FILES.length === 1 ? SUBJECT_FILES[0] : null);
    if (!file) { this.setState({ subjectRows: null }); return; }
    try {
      const resp = await fetch(file.url);
      const buf = await resp.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      this.setState({ subjectRows: XLSX.utils.sheet_to_json(ws, { defval: "" }) });
    } catch (e) { console.error("load subject file", e); this.setState({ subjectRows: null }); }
  }

  // Columns derived from the actual data: base (non-Q) columns + Q columns.
  getColumns = () => this.deriveColumns(this.enrichedRows());

  // Results rows matching the Class / Section filters.
  getFiltered = () => this.filterByScope(this.enrichedRows());

  downloadExcel = () => {
    const isSub = this.state.activeTab === "subjects";
    const source = isSub ? this.enrich(this.state.subjectRows || []) : this.enrichedRows();
    const { base, q } = this.deriveColumns(source);
    // Results: respect the question-wise toggle. Subject-wise: all its columns. Never export "File".
    const allCols = (isSub || this.state.showQuestions ? [...base, ...q] : base).filter((c) => String(c).toLowerCase().trim() !== "file");
    const rowsSrc = this.filterByScope(source);
    const rows = rowsSrc.map((r) => {
      const o = {};
      allCols.forEach((c) => { o[c] = r[c] !== undefined && r[c] !== null ? r[c] : ""; });
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(rows, { header: allCols });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isSub ? "Subject-wise" : "OMR Results");
    const stamp = new Date().toLocaleDateString().replace(/\//g, "-");
    XLSX.writeFile(wb, `${isSub ? "OMR_Subjectwise" : "OMR_Results"}_${stamp}.xlsx`);
  };

  // Subject-wise contact list: S.NO | SEC | PHONE1 | STUDENT ID | <subject> Mk… | Total Marks,
  // with a dynamic title block (institution :: branch, then the selected exam).
  downloadContactList = () => {
    const map = this.state.rollMap || {};
    // Export every student in the subject-wise file (no Class/Section scope filter),
    // so rows whose section isn't in the dropdown options are not dropped.
    const rows = this.enrich(this.state.subjectRows || []);
    const idOf = (r) => {
      const raw = r["Roll Number"] != null && r["Roll Number"] !== "" ? r["Roll Number"] : r["CANDIDATE ID"];
      return raw != null ? String(raw).trim() : "";
    };

    // Subject marks columns ("PHYSICS Mk", "MATHS Mk", …) in file order + Total Marks.
    const keys = [];
    rows.forEach((r) => Object.keys(r).forEach((k) => { if (keys.indexOf(k) === -1) keys.push(k); }));
    const mkCols = keys.filter((k) => /\bMk$/i.test(String(k).trim()));
    const totalCol = keys.find((k) => String(k).toLowerCase().trim() === "total marks");
    const markCols = totalCol ? [...mkCols, totalCol] : mkCols;

    // Rank by Total Marks (desc). Competition ranking: equal totals share a rank,
    // the next distinct total skips accordingly (1,2,2,4…). Blank totals get no rank.
    const num = (v) => { const n = parseFloat(String(v).replace(/,/g, "")); return Number.isNaN(n) ? null : n; };
    const totals = rows.map((r) => (totalCol ? num(r[totalCol]) : null));
    const order = rows.map((_, i) => i).sort((a, b) => {
      const ta = totals[a], tb = totals[b];
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta;
    });
    const rankByIndex = {};
    let lastVal = null, lastRank = 0;
    order.forEach((idx, pos) => {
      const v = totals[idx];
      if (v === null) { rankByIndex[idx] = ""; return; }
      if (v === lastVal) { rankByIndex[idx] = lastRank; }
      else { lastRank = pos + 1; lastVal = v; rankByIndex[idx] = lastRank; }
    });

    const dataRows = rows.map((r, i) => {
      const id = idOf(r);
      const hit = map[id] || {};
      const name = r["Student Name"] || hit.name || r["CANDIDATE NAME"] || "";
      const base = [i + 1, r.Section || hit.sectionName || "", hit.phone || "", id, name];
      return [...base, ...markCols.map((c) => (r[c] !== undefined && r[c] !== null ? r[c] : "")), rankByIndex[i]];
    });

    const examName = this.state.filters.exam || this.state.uploadName || "";

    // Display headers strip the trailing " Mk" (e.g. "PHYSICS Mk" -> "PHYSICS").
    const markHeaders = markCols.map((c) => String(c).replace(/\s*Mk$/i, "").trim());
    const header = ["S.NO.", "SEC", "PHONE1", "STUDENT ID", "STUDENT NAME", ...markHeaders, "RANK"];
    const lastCol = header.length - 1;

    const aoa = [];
    const merges = [];
    let r = 0;
    const titleRow = (text) => { aoa.push([text]); merges.push({ s: { r, c: 0 }, e: { r, c: lastCol } }); r++; };
    if (examName) titleRow(examName);
    if (aoa.length) { aoa.push([]); r++; }
    aoa.push(header);
    dataRows.forEach((d) => aoa.push(d));

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 7 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 24 }, ...markCols.map(() => ({ wch: 12 })), { wch: 8 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contact List");
    const stamp = new Date().toLocaleDateString().replace(/\//g, "-");
    XLSX.writeFile(wb, `OMR_ContactList_${stamp}.xlsx`);
  };

  render() {
    const all = this.allRows();
    const rows = this.getFiltered();
    const { base, q } = this.getColumns();
    const cols = this.state.showQuestions ? [...base, ...q] : base;
    const f = this.state.filters;
    const setFilter = (key, value) => this.setState({ filters: { ...this.state.filters, [key]: value }, page: 1 });
    const S = {
      wrap: { background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", padding: "20px 24px 60px", minHeight: "70vh" },
      card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "18px 20px", marginBottom: 16 },
      header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
      hLeft: { display: "flex", alignItems: "center", gap: 12 },
      iconChip: { width: 46, height: 46, borderRadius: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 },
      h1: { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 },
      sub: { fontSize: 13, color: "#64748b", marginTop: 2 },
      btn: { background: "#16a34a", border: "none", borderRadius: 9, padding: "10px 18px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" },
      th: { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", padding: "9px 10px", borderBottom: "2px solid #ede9fe", whiteSpace: "nowrap" },
      td: { fontSize: 13, color: "#334155", padding: "8px 10px", borderBottom: "1px solid #f3e8ff", whiteSpace: "nowrap" },
      pageBtn: { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "6px 11px", fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer", minWidth: 34 },
      pageBtnActive: { background: "#7c3aed", borderColor: "#7c3aed", color: "#fff" },
      pageBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
    };

    // ── Sorting + pagination for the Results table ──
    const sortedRows = this.sortRows(rows);
    const total = sortedRows.length;
    const pageSize = this.state.pageSize;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(this.state.page, totalPages);
    const startIdx = (page - 1) * pageSize;
    const pageRows = sortedRows.slice(startIdx, startIdx + pageSize);
    const sortIcon = (c) => this.state.sortKey !== c
      ? <i className="fal fa-sort" style={{ marginLeft: 6, opacity: 0.4 }} />
      : <i className={`fal fa-sort-${this.state.sortDir === "asc" ? "up" : "down"}`} style={{ marginLeft: 6 }} />;
    // Compact page-number window around the current page.
    const pageNums = [];
    { const win = 2; let s = Math.max(1, page - win); let e = Math.min(totalPages, page + win);
      if (page <= win) e = Math.min(totalPages, 1 + win * 2);
      if (page > totalPages - win) s = Math.max(1, totalPages - win * 2);
      for (let p = s; p <= e; p++) pageNums.push(p); }

    return (
      <div style={S.wrap}>
        <div style={S.card}>
          <div style={S.header}>
            <div style={S.hLeft}>
              <span style={S.iconChip}><i className="fal fa-scanner-image" /></span>
              <div>
                <h1 style={S.h1}>OMR Results</h1>
                <div style={S.sub}>
                  {this.state.uploadName
                    ? <span><i className="fal fa-file-excel" style={{ color: "#16a34a" }} /> {this.state.uploadName} · {rows.length} rows</span>
                    : `${rows.length} row${rows.length === 1 ? "" : "s"} · select an exam`}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[{ k: "results", label: "Results", icon: "fal fa-table" }, { k: "subjects", label: "Subject-wise Report", icon: "fal fa-layer-group" }].map((t) => {
            const on = this.state.activeTab === t.k;
            return (
              <button key={t.k} onClick={() => this.setTab(t.k)}
                style={{ background: on ? "#7c3aed" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#7c3aed" : "#e2e8f0"), borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                <i className={t.icon} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Filters: Exam (static) / Class + Section (dynamic, like Management Report) */}
        <div style={S.card}>
          {(() => {
            const sc = this.state.scope || {};
            const classByName = {}; (sc.classes || []).forEach((c) => { classByName[c.name] = c.id; });
            const selClassId = f.class ? classByName[f.class] : null;
            const sectionOpts = (sc.sections || []).filter((s) => !selClassId || String(s.class_id) === String(selClassId));
            const selStyle = { borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "8px 10px", fontSize: 13, color: "#1f2937", background: "#f9fafb", minWidth: 160 };
            const lbl = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 };
            return (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={lbl}>Exam</label>
                  <select value={f.exam} onChange={(e) => { setFilter("exam", e.target.value); this.loadExamFile(e.target.value); }} style={selStyle}>
                    <option value="">{LOCAL_FILES.length ? "Select Exam" : "No exam files"}</option>
                    {LOCAL_FILES.map((fl) => <option key={fl.name} value={fl.name}>{fl.name}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={lbl}>Class</label>
                  <select value={f.class} onChange={(e) => this.onClassChange(e.target.value)} style={selStyle}>
                    <option value="">All Classes</option>
                    {(sc.classes || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }} ref={this.sectionRef}>
                  <label style={lbl}>Section</label>
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => this.setState({ sectionOpen: !this.state.sectionOpen })}
                      style={{ ...selStyle, minWidth: 180, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                    >
                      <span style={{ color: f.section.length ? "#1f2937" : "#9ca3af" }}>
                        {!f.section.length ? "None" : (sectionOpts.length && sectionOpts.every((s) => f.section.includes(s.name)) ? `All Sections (${f.section.length})` : `${f.section.length} selected`)}
                      </span>
                      <i className="fal fa-chevron-down" style={{ fontSize: 11, color: "#94a3b8", marginLeft: 8 }} />
                    </div>
                    {this.state.sectionOpen && (
                      <div style={{ position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0, minWidth: 200, maxHeight: 240, overflowY: "auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.12)", padding: "6px" }}>
                        {sectionOpts.length === 0 ? (
                          <div style={{ padding: "10px 12px", fontSize: 13, color: "#94a3b8" }}>No sections</div>
                        ) : (
                          <React.Fragment>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #f1f5f9", marginBottom: 2 }}>
                              <input
                                type="checkbox"
                                checked={sectionOpts.length > 0 && sectionOpts.every((s) => f.section.includes(s.name))}
                                onChange={(e) => setFilter("section", e.target.checked ? sectionOpts.map((s) => s.name) : [])}
                                style={{ accentColor: "#7c3aed" }}
                              />
                              Select All
                            </label>
                            {sectionOpts.map((s) => {
                              const on = f.section.includes(s.name);
                              return (
                                <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, cursor: "pointer", fontSize: 13, color: "#334155", background: on ? "#f5f3ff" : "transparent" }}>
                                  <input type="checkbox" checked={on} onChange={() => setFilter("section", on ? f.section.filter((x) => x !== s.name) : [...f.section, s.name])} style={{ accentColor: "#7c3aed" }} />
                                  {s.name}
                                </label>
                              );
                            })}
                            {f.section.length > 0 && (
                              <div onClick={() => setFilter("section", [])} style={{ padding: "7px 10px", fontSize: 12, fontWeight: 600, color: "#dc2626", cursor: "pointer", borderTop: "1px solid #f1f5f9", marginTop: 4 }}>Clear all</div>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{rows.length} of {all.length}</span>
                  {this.state.activeTab === "results" && (
                    <button
                      style={{ background: this.state.showQuestions ? "#7c3aed" : "#fff", color: this.state.showQuestions ? "#fff" : "#7c3aed", border: "1.5px solid #7c3aed", borderRadius: 9, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                      onClick={() => this.setState({ showQuestions: !this.state.showQuestions })}
                    >
                      <i className="fal fa-list-ol" /> {this.state.showQuestions ? "Hide Questions" : "Show Question-wise"}
                    </button>
                  )}
                  {this.state.activeTab === "subjects" && (
                    <button style={{ ...S.btn, background: "#7c3aed", padding: "9px 14px", whiteSpace: "nowrap" }} onClick={this.downloadContactList}>
                      <i className="fal fa-address-book" /> Download Marks Report
                    </button>
                  )}
                  <button style={{ ...S.btn, padding: "9px 14px", whiteSpace: "nowrap" }} onClick={this.downloadExcel}>
                    <i className="fal fa-file-excel" /> Download Excel
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Results table OR Subject-wise report */}
        {this.state.activeTab === "results" ? (
        <div style={S.card}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{cols.map((c) => (
                  <th key={c} style={{ ...S.th, cursor: "pointer", userSelect: "none" }} onClick={() => this.onSort(c)} title="Sort">
                    <span style={{ display: "inline-flex", alignItems: "center" }}>{c}{sortIcon(c)}</span>
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {total === 0 ? (
                  <tr><td colSpan={cols.length} style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: "28px 0" }}>No OMR results.</td></tr>
                ) : pageRows.map((r, i) => (
                  <tr key={startIdx + i} style={{ background: (startIdx + i) % 2 ? "#faf5ff" : "#fff" }}>
                    {cols.map((c) => {
                      const isQ = isQCol(c);
                      const val = r[c] !== undefined && r[c] !== null && r[c] !== "" ? r[c] : (isQ ? "" : "-");
                      return (
                        <td key={c} style={{ ...S.td, textAlign: isQ ? "center" : "left", fontWeight: c === "File" || c === "Roll Number" || c === "Student Name" ? 600 : (isQ ? 700 : 400), color: isQ ? "#7c3aed" : (c === "File" || c === "Roll Number" || c === "Student Name" ? "#0f172a" : "#334155") }}>
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination bar */}
          {total > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 14, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#64748b" }}>
                <span>Showing <strong style={{ color: "#0f172a" }}>{startIdx + 1}–{Math.min(startIdx + pageSize, total)}</strong> of <strong style={{ color: "#0f172a" }}>{total}</strong></span>
                <span style={{ color: "#cbd5e1" }}>|</span>
                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  Rows
                  <select value={pageSize} onChange={(e) => this.setPageSize(Number(e.target.value))} style={{ borderRadius: 8, border: "1.5px solid #e5e7eb", padding: "5px 8px", fontSize: 13, color: "#1f2937", background: "#f9fafb" }}>
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }} disabled={page === 1} onClick={() => this.setPage(1)} title="First">&laquo;</button>
                <button style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }} disabled={page === 1} onClick={() => this.setPage(page - 1)} title="Previous">&lsaquo;</button>
                {pageNums.map((p) => (
                  <button key={p} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }} onClick={() => this.setPage(p)}>{p}</button>
                ))}
                <button style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }} disabled={page === totalPages} onClick={() => this.setPage(page + 1)} title="Next">&rsaquo;</button>
                <button style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }} disabled={page === totalPages} onClick={() => this.setPage(totalPages)} title="Last">&raquo;</button>
              </div>
            </div>
          )}
        </div>
        ) : this.renderSubjectReport(S)}
      </div>
    );
  }
}

export default withApollo(OmrResultsSection);
